import { useCallback, useEffect, useRef } from 'react';
import { getEnvName } from '../utils/env';

const BATCH_SIZE = 20;
const FLUSH_INTERVAL_MS = 15_000;
const ENDPOINT = '/api/logs/client-nav';

// Module-scoped queue so multiple hook instances share one flush loop.
const queue = [];
let flushTimer = null;
let flushing = false;

// The app stores its JWT as 'nh_auth_token' — see api.js, authService.js,
// useFlowSocket.js and queueSocketClient.js, which all use that one key. This read
// asked for 'token', which nothing ever writes, so it always came back null, no
// Authorization header was sent, and /api/logs/client-nav (behind `authenticate`)
// answered 401 to every flush: every 15s, again on each batch of 20 events, and
// again on unload. Two costs — nav analytics has recorded nothing since it shipped,
// and the console was loud enough to bury real errors, which is how this was found
// on 2026-10-06 while chasing an unrelated 403.
const TOKEN_KEY = 'nh_auth_token';

function readAuthToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

async function flush() {
  if (flushing || queue.length === 0) return;
  flushing = true;
  const batch = queue.splice(0, BATCH_SIZE);
  const token = readAuthToken();
  try {
    await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    });
  } catch {
    // Silently drop — nav analytics is best-effort.
  } finally {
    flushing = false;
  }
}

function ensureTimer() {
  if (flushTimer) return;
  flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (queue.length === 0) return;
    const token = readAuthToken();
    const body = JSON.stringify({ events: queue.splice(0, queue.length) });
    try {
      // sendBeacon is more reliable on unload but can't set Authorization headers,
      // so fall back to fetch keepalive if we have a token.
      if (token && 'fetch' in window) {
        fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body,
          keepalive: true,
        });
      } else if ('sendBeacon' in navigator) {
        navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
      }
    } catch {}
  });
}

export function useNavAnalytics() {
  const envRef = useRef(getEnvName());

  useEffect(() => {
    ensureTimer();
  }, []);

  const track = useCallback((eventName, payload = {}) => {
    queue.push({
      event: eventName,
      env: envRef.current,
      ts: new Date().toISOString(),
      path: typeof window !== 'undefined' ? window.location.pathname : null,
      ...payload,
    });
    if (queue.length >= BATCH_SIZE) flush();
  }, []);

  return { track };
}
